import { createApp } from 'vue'
import { createPinia } from 'pinia'
import * as Sentry from "@sentry/vue";
import App from './App.vue'
import router from './router'
import components from './components'
import { setupValidationRules } from './helpers/validationRules'
import { isSentryEnabled, safeSentryCapture } from './helpers/sentryUtils'
import i18n from './translate'
import auth from './auth';

import './assets/style.css'
setupValidationRules()

// Global error handler for module loading failures
let reloadAttempted = false; // Prevent infinite reload loops

window.addEventListener('error', (event) => {
  if (event.message && (event.message.includes('Loading CSS chunk') || 
      event.message.includes('Loading chunk') ||
      event.message.includes('Importing a module script failed') ||
      event.message.includes('bare specifier'))) {
    
    // Log to Sentry with context (respects environment flag)
    safeSentryCapture(Sentry.captureException, new Error(event.message), {
      tags: {
        error_type: 'module_loading_error',
        error_source: 'global_error_handler'
      },
      extra: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
        user_agent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        reload_attempted: reloadAttempted
      },
      level: 'error'
    });
    
    console.error('Module loading error detected:', event.message);
    
    // Only reload once per session to prevent infinite loops
    if (!reloadAttempted) {
      reloadAttempted = true;
      console.log('Attempting page reload to fix module loading...');
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  }
});

// Handle unhandled promise rejections (for dynamic imports)
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason && event.reason.message && 
      (event.reason.message.includes('Loading chunk') || 
       event.reason.message.includes('Importing a module script failed') ||
       event.reason.message.includes('bare specifier'))) {
    
    // Log to Sentry with context (respects environment flag)
    safeSentryCapture(Sentry.captureException, event.reason, {
      tags: {
        error_type: 'module_import_rejection',
        error_source: 'unhandled_rejection'
      },
      extra: {
        promise: event.promise,
        reason: event.reason.message,
        stack: event.reason.stack,
        user_agent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        reload_attempted: reloadAttempted
      },
      level: 'error'
    });
    
    console.error('Module import rejection detected:', event.reason.message);
    event.preventDefault(); // Prevent the error from being logged to console
    
    // Only reload once per session to prevent infinite loops
    if (!reloadAttempted) {
      reloadAttempted = true;
      console.log('Attempting page reload to fix module import rejection...');
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  }
});

const app = createApp(App)

// Only initialize Sentry if enabled
if (isSentryEnabled()) {
  Sentry.init({
    app,
    dsn: "https://d17c24c14174d81c7a7fc8fd1c0e1282@o415521.ingest.us.sentry.io/4509933190447104",
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true
  });
  console.log('Sentry initialized for environment:', import.meta.env.VITE_APP_ENV || import.meta.env.MODE);
} else {
  console.log('Sentry disabled for environment:', import.meta.env.VITE_APP_ENV || import.meta.env.MODE);
}

app.use(auth)
const pinia = createPinia()

app.use(i18n())
app.use(pinia)
app.use(router)
for (const component of components) {
  app.component(component.name, component)
}

app.mount("#app");
